// libs
import cx from "classnames";
import React, { ReactElement, useCallback, useState, useRef, RefObject, useEffect } from "react";

// internal-deps
import {
    BottomSheet,
    BottomSheetChildrenProps,
    useFocusDialog,
    getActiveElement,
    useFocusTrap,
} from "@swiggy-private/core-ui";

// widgets
import { Button, BUTTON_ACCENT } from "@payment-ui/widgets/button";

//constants
import { ACCESSIBILITY_ROLES } from "@payment-ui/constants/index";
import { PAYMENT_ALERT_HEADER_TITLE_ID, PAYMENT_ALERT_CONTENT_ID } from "@payment-ui/constants/PaymentAlert";

// test-ids
import { PAYMENT_ALERT_TEST_IDS } from "@swiggy-private/payment-test-ids";

// styles
import styles from "./DeleteCardPrompt.scss";

export interface DeleteCardActionProps {
    text: string;
    onClick: () => void;
}

export interface DeleteCardProps {
    title: string;
    message: string;
    primaryAction?: DeleteCardActionProps;
    secondaryAction?: DeleteCardActionProps;
    hideOnOverlayClick?: boolean;
    onClose?: VoidFunction;
}

type DeleteCardContentProps = Omit<DeleteCardProps, "hideOnOverlayClick" | "onClose"> & {
    setAction: (fn: VoidFunction) => void;
};

const DeleteCardContent = React.memo(function DeleteCardContent(
    props: DeleteCardContentProps & BottomSheetChildrenProps,
): React.ReactElement {
    const headerDivRef = useRef<HTMLElement | null>(null);
    const previouslyFocusedElement = useRef<HTMLElement | null>(null);
    useEffect(() => {
        previouslyFocusedElement.current = getActiveElement();
    }, []);

    useFocusDialog(headerDivRef, previouslyFocusedElement);

    const handlePrimaryActionClick = (): void => {
        if (props.primaryAction) {
            props.setAction(props.primaryAction.onClick);
            props.close();
        }
    };

    const handleSecondaryActionClick = (): void => {
        if (!props.secondaryAction) {
            props.close();
            return;
        }

        // setting after close action to secondary action handler
        props.setAction(props.secondaryAction.onClick);
        props.close();
    };

    const actionClass = cx(styles.deleteCardAction, {
        [styles.deleteCardActionSingle]: !props.secondaryAction,
    });

    const secondaryActionClass = cx(styles.deleteCardAction, styles.deleteCardActionSecondary, {
        [styles.deleteCardContentRowSecondaryActionCta]: true,
    });

    return (
        <div
            role={ACCESSIBILITY_ROLES.ALERTDIALOG}
            aria-labelledby={PAYMENT_ALERT_HEADER_TITLE_ID}
            aria-describedby={PAYMENT_ALERT_CONTENT_ID}
        >
            <div className={styles.deleteCardHeader} data-testid={PAYMENT_ALERT_TEST_IDS.HEADER}>
                <div className={styles.deleteCardHeaderInfo}>
                    <div className={styles.deleteCardHeaderContainer}>
                        <div>
                            <div
                                id={PAYMENT_ALERT_HEADER_TITLE_ID}
                                className={styles.deleteCardHeaderTitle}
                                data-testid={PAYMENT_ALERT_TEST_IDS.HEADER_TITLE}
                                ref={headerDivRef as RefObject<HTMLDivElement>}
                                tabIndex={0}
                                role={ACCESSIBILITY_ROLES.HEADING}
                                aria-level={3}
                            >
                                {props.title}
                            </div>
                            <div
                                className={styles.deleteCardHeaderMessage}
                                data-testid={PAYMENT_ALERT_TEST_IDS.HEADER_MESSAGE}
                            >
                                {props.message}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.deleteCardContent} data-testid={PAYMENT_ALERT_TEST_IDS.CONTENT}>
                {props.primaryAction && (
                    <div className={cx(styles.deleteCardContent, styles.deleteCardContentRowPrimaryAction)}>
                        <Button
                            onClick={handlePrimaryActionClick}
                            accent={BUTTON_ACCENT.SWIGGY_ORANGE_LARGE}
                            className={actionClass}
                            data-testid={PAYMENT_ALERT_TEST_IDS.PRIMARY_ACTION_BUTTON}
                        >
                            <div className={styles.deleteCardActionMain}>{props.primaryAction.text}</div>
                        </Button>
                    </div>
                )}

                {props.secondaryAction && (
                    <div className={styles.deleteCardContentRowSecondaryAction}>
                        <Button
                            onClick={handleSecondaryActionClick}
                            accent={BUTTON_ACCENT.SWIGGY_ORANGE_TRANSPARENT}
                            className={secondaryActionClass}
                            data-testid={PAYMENT_ALERT_TEST_IDS.SECONDARY_ACTION_BUTTON}
                        >
                            <div className={styles.deleteCardActionMain}>{props.secondaryAction.text}</div>
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
});

function DeleteCardPrompt(props: DeleteCardProps): ReactElement {
    const { hideOnOverlayClick = true, onClose, ..._props } = props;
    const [actionOnClose, setActionOnClose] = useState<VoidFunction | undefined>();
    const _onClose = useCallback((): void => {
        actionOnClose && actionOnClose();
        onClose && onClose();
    }, [onClose, actionOnClose]);

    useFocusTrap();
    return (
        <BottomSheet hideOnOverlayClick={hideOnOverlayClick} onClose={_onClose}>
            {(bProps: BottomSheetChildrenProps): React.ReactElement => (
                <DeleteCardContent {...bProps} {..._props} setAction={setActionOnClose} />
            )}
        </BottomSheet>
    );
}

export default React.memo(DeleteCardPrompt);
